// src/roles.ts
import { z } from "zod";
var ROLES = ["client", "provider", "admin"];
var roleSchema = z.enum(ROLES);
var ADMIN_PERMISSIONS = ["finance", "moderation", "support"];
var adminPermissionSchema = z.enum(ADMIN_PERMISSIONS);

// src/money.ts
var ORE_PER_SEK = 100;
function sekToOre(sek) {
  if (!Number.isFinite(sek)) throw new RangeError("Amount must be a finite number");
  return Math.round(sek * ORE_PER_SEK);
}
function oreToSek(ore) {
  if (!Number.isInteger(ore)) throw new RangeError("\xD6re must be a whole number");
  return ore / ORE_PER_SEK;
}
function formatSek(ore, locale = "sv-SE") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "SEK",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(oreToSek(ore));
}
function percentOfOre(ore, basisPoints) {
  if (!Number.isInteger(ore) || !Number.isInteger(basisPoints)) {
    throw new RangeError("\xD6re and basis points must be whole numbers");
  }
  return Math.round(ore * basisPoints / 1e4);
}

// src/api-error.ts
var API_ERROR_CODES = [
  "VALIDATION_FAILED",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "RATE_LIMITED",
  "PAYLOAD_TOO_LARGE",
  "BAD_REQUEST",
  "INTERNAL_ERROR"
];

// src/pagination.ts
import { z as z2 } from "zod";
var MAX_PAGE_SIZE = 100;
var paginationQuerySchema = z2.object({
  page: z2.coerce.number().int().min(1).default(1),
  pageSize: z2.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(20)
});

// src/auth.ts
import { z as z4 } from "zod";

// src/phone.ts
import { z as z3 } from "zod";
function toE164(input) {
  const compact = input.replace(/[\s\-().]/g, "");
  let digits;
  if (compact.startsWith("+")) digits = compact.slice(1);
  else if (compact.startsWith("00")) digits = compact.slice(2);
  else if (compact.startsWith("0")) digits = `46${compact.slice(1)}`;
  else return null;
  if (digits.startsWith("460")) digits = `46${digits.slice(3)}`;
  if (!/^[1-9]\d{7,14}$/.test(digits)) return null;
  return `+${digits}`;
}
var optionalPhoneSchema = z3.string().trim().max(30).transform((value, ctx) => {
  if (value === "") return null;
  const phone = toE164(value);
  if (!phone) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Enter a valid phone number, e.g. 070-123 45 67"
    });
    return z3.NEVER;
  }
  return phone;
});
var phoneSchema = z3.string().trim().max(30).transform((value, ctx) => {
  const phone = toE164(value);
  if (!phone) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      message: "Enter a valid phone number, e.g. 070-123 45 67"
    });
    return z3.NEVER;
  }
  return phone;
});

// src/auth.ts
var LOCALES = ["sv", "en"];
var localeSchema = z4.enum(LOCALES);
var loginSchema = z4.object({
  email: z4.string().trim().toLowerCase().email().max(254),
  // An upper bound keeps hashing cheap for absurdly long inputs
  password: z4.string().min(1).max(200)
});
var newPasswordSchema = z4.string().min(10).max(200);
var forgotPasswordSchema = z4.object({
  email: z4.string().trim().toLowerCase().email().max(254)
});
var passwordTokenSchema = z4.string().min(20).max(128);
var checkPasswordLinkSchema = z4.object({ token: passwordTokenSchema });
var setPasswordSchema = z4.object({ token: passwordTokenSchema, password: newPasswordSchema });
var updateProfileSchema = z4.object({
  name: z4.string().trim().min(2).max(100),
  phone: optionalPhoneSchema
});
var changePasswordSchema = z4.object({
  currentPassword: z4.string().min(1).max(200),
  newPassword: newPasswordSchema
});
var updatePreferencesSchema = z4.object({ locale: localeSchema.optional(), sidebarCollapsed: z4.boolean().optional() }).refine((value) => value.locale !== void 0 || value.sidebarCollapsed !== void 0, {
  message: "Send at least one preference."
});

// src/notifications.ts
import { z as z5 } from "zod";
var NOTIFICATION_TYPES = [
  "new_job_match",
  "new_proposal",
  "job_expiring",
  "job_no_proposals",
  "proposal_accepted",
  "new_message",
  "proposal_chosen",
  "job_closed",
  "moderation_warning",
  "trial_ending",
  "payment_failed",
  "grace_ending",
  "access_paused"
];
var notificationTypeSchema = z5.enum(NOTIFICATION_TYPES);
var jobTitle = z5.string().trim().min(1).max(200);
var notificationDataSchemas = {
  /** A new open job a provider can quote on (P4-02 matching) */
  new_job_match: z5.object({
    jobId: z5.string().uuid(),
    title: jobTitle,
    /** Municipality or area name shown with the job */
    area: z5.string().trim().min(1).max(100)
  }),
  /** A provider sent a proposal on the client's job */
  new_proposal: z5.object({
    jobId: z5.string().uuid(),
    proposalId: z5.string().uuid(),
    title: jobTitle,
    providerName: z5.string().trim().min(1).max(200)
  }),
  /** The client accepted the provider's proposal and a conversation opened (P5-02) */
  proposal_accepted: z5.object({
    jobId: z5.string().uuid(),
    proposalId: z5.string().uuid(),
    title: jobTitle,
    clientName: z5.string().trim().min(1).max(200)
  }),
  /** Unread messages in a conversation, sent a few minutes after the first one if still unread (P5-03) */
  new_message: z5.object({
    conversationId: z5.string().uuid(),
    /** The recipient's side, for the link */
    side: z5.enum(["client", "provider"]),
    title: jobTitle,
    senderName: z5.string().trim().min(1).max(200)
  }),
  /** The client chose this provider for the job (P5-05) */
  proposal_chosen: z5.object({
    jobId: z5.string().uuid(),
    proposalId: z5.string().uuid(),
    title: jobTitle,
    clientName: z5.string().trim().min(1).max(200)
  }),
  /** A job the provider quoted on closed without them: another provider was chosen, or the client cancelled */
  job_closed: z5.object({
    jobId: z5.string().uuid(),
    proposalId: z5.string().uuid(),
    title: jobTitle,
    reason: z5.enum(["other_chosen", "cancelled"])
  }),
  /** Hemmafixa warns the user after a report (P5-06); the admin's note is not included */
  moderation_warning: z5.object({ reason: z5.enum(["spam", "abusive", "fraud", "off_platform", "other"]) }),
  /** The client's open job closes soon (P4-09 scheduled sweep, day 25 of 30) */
  job_expiring: z5.object({
    jobId: z5.string().uuid(),
    title: jobTitle,
    /** When the job expires, ISO 8601 */
    expiresAt: z5.string().datetime({ offset: true })
  }),
  /** The client's open job has had no proposals yet (P4-09 scheduled sweep) */
  job_no_proposals: z5.object({
    jobId: z5.string().uuid(),
    title: jobTitle,
    /** Whole days since the job opened */
    days: z5.number().int().min(1).max(365)
  }),
  /** The provider's trial ends soon (Stripe's trial_will_end, 3 days before; P6-05) */
  trial_ending: z5.object({ endsAt: z5.string().datetime({ offset: true }) }),
  /** A subscription payment failed; the grace period runs until graceEndsAt (P6-05) */
  payment_failed: z5.object({ graceEndsAt: z5.string().datetime({ offset: true }) }),
  /** Reminder shortly before the grace period ends (P6-05 sweep) */
  grace_ending: z5.object({ graceEndsAt: z5.string().datetime({ offset: true }) }),
  /** The grace period ended unpaid: no new proposals until paid; conversations go on (P6-05) */
  access_paused: z5.object({})
};
var notificationInputSchema = z5.discriminatedUnion("type", [
  z5.object({ type: z5.literal("new_job_match"), data: notificationDataSchemas.new_job_match }),
  z5.object({ type: z5.literal("new_proposal"), data: notificationDataSchemas.new_proposal }),
  z5.object({ type: z5.literal("job_expiring"), data: notificationDataSchemas.job_expiring }),
  z5.object({ type: z5.literal("job_no_proposals"), data: notificationDataSchemas.job_no_proposals }),
  z5.object({ type: z5.literal("proposal_accepted"), data: notificationDataSchemas.proposal_accepted }),
  z5.object({ type: z5.literal("new_message"), data: notificationDataSchemas.new_message }),
  z5.object({ type: z5.literal("proposal_chosen"), data: notificationDataSchemas.proposal_chosen }),
  z5.object({ type: z5.literal("job_closed"), data: notificationDataSchemas.job_closed }),
  z5.object({ type: z5.literal("moderation_warning"), data: notificationDataSchemas.moderation_warning }),
  z5.object({ type: z5.literal("trial_ending"), data: notificationDataSchemas.trial_ending }),
  z5.object({ type: z5.literal("payment_failed"), data: notificationDataSchemas.payment_failed }),
  z5.object({ type: z5.literal("grace_ending"), data: notificationDataSchemas.grace_ending }),
  z5.object({ type: z5.literal("access_paused"), data: notificationDataSchemas.access_paused })
]);
var notificationListQuerySchema = paginationQuerySchema.extend({
  // Not z.coerce.boolean(): that turns the string "false" into true
  unread: z5.enum(["true", "false"]).optional().transform((value) => value === "true")
});
var MAX_MARK_READ_IDS = 100;
var markNotificationsReadSchema = z5.union([
  z5.object({ ids: z5.array(z5.string().uuid()).min(1).max(MAX_MARK_READ_IDS) }).strict(),
  z5.object({ all: z5.literal(true) }).strict()
]);
var DEFAULT_NOTIFICATION_PREFERENCES = {
  emailEnabled: true,
  smsEnabled: true
};
var updateNotificationPreferencesSchema = z5.object({ emailEnabled: z5.boolean().optional(), smsEnabled: z5.boolean().optional() }).strict().refine((value) => value.emailEnabled !== void 0 || value.smsEnabled !== void 0, {
  message: "Nothing to change"
});

// src/users.ts
import { z as z6 } from "zod";
var USER_STATUSES = ["active", "suspended"];
var userStatusSchema = z6.enum(USER_STATUSES);
var idParamSchema = z6.object({ id: z6.string().uuid() });
var suspendUserSchema = z6.object({ reason: z6.string().trim().min(3).max(500) });
var reactivateUserSchema = z6.object({ note: z6.string().trim().max(500).optional() });

// src/jobs.ts
import { z as z7 } from "zod";
var JOB_STATUSES = [
  "draft",
  "in_review",
  "needs_info",
  "open",
  "provider_chosen",
  "rejected",
  "cancelled",
  "expired"
];
var jobStatusSchema = z7.enum(JOB_STATUSES);
var WANTED_STARTS = ["asap", "within_month", "flexible"];
var wantedStartSchema = z7.enum(WANTED_STARTS);
var JOB_QUESTION_SOURCES = ["ai", "admin"];
var jobQuestionSourceSchema = z7.enum(JOB_QUESTION_SOURCES);
var JOB_QUESTION_ANSWER_TYPES = ["text", "yes_no", "number", "choice"];
var jobQuestionAnswerTypeSchema = z7.enum(JOB_QUESTION_ANSWER_TYPES);
var MAX_FOLLOW_UP_QUESTIONS = 5;
var questionText = z7.string().trim().min(5).max(200);
var optional = (schema) => schema.nullish().transform((value) => value ?? void 0).optional();
var followUpQuestionSchema = z7.discriminatedUnion("answerType", [
  z7.object({ answerType: z7.literal("text"), question: questionText }),
  z7.object({ answerType: z7.literal("yes_no"), question: questionText }),
  z7.object({
    answerType: z7.literal("number"),
    question: questionText,
    unit: optional(z7.string().trim().min(1).max(10))
  }),
  z7.object({
    answerType: z7.literal("choice"),
    question: questionText,
    options: z7.array(z7.string().trim().min(1).max(80)).min(2).max(5).refine((options) => new Set(options).size === options.length, "Options must be different")
  })
]);
var followUpSuggestionSchema = z7.object({
  suggestedTitle: optional(z7.string().trim().min(5).max(100)),
  questions: z7.array(followUpQuestionSchema).max(MAX_FOLLOW_UP_QUESTIONS)
});

// src/job-drafts.ts
import { z as z10 } from "zod";

// src/catalogue.ts
import { z as z8 } from "zod";
var postalCodeSchema = z8.string().transform((value) => value.replace(/\s/g, "")).pipe(z8.string().regex(/^\d{5}$/, "Enter a postal code with 5 digits, e.g. 412 56"));
var postalCodeParamSchema = z8.object({ code: postalCodeSchema });
var formatPostalCode = (code) => `${code.slice(0, 3)} ${code.slice(3)}`;

// src/geo.ts
import { z as z9 } from "zod";
var jobLocationSchema = z9.object({
  lat: z9.number().min(55).max(69.1),
  lng: z9.number().min(10.5).max(24.2)
});
var REGIONS = [
  [10, 59.33, 18.07],
  // Stockholm
  [15, 59.2, 17.63],
  // Södertälje
  [17, 59.36, 17.97],
  // Solna
  [19, 59.62, 17.72],
  // Märsta
  [20, 55.6, 13],
  // Malmö
  [22, 55.7, 13.19],
  // Lund
  [25, 56.05, 12.69],
  // Helsingborg
  [27, 55.43, 13.82],
  // Ystad
  [29, 56.03, 14.16],
  // Kristianstad
  [30, 56.67, 12.86],
  // Halmstad
  [33, 57.18, 13.95],
  // Värnamo area
  [35, 56.88, 14.81],
  // Växjö
  [37, 56.16, 15.59],
  // Karlskrona
  [39, 56.66, 16.36],
  // Kalmar
  [40, 57.71, 11.97],
  // Göteborg
  [43, 57.49, 12.08],
  // Kungsbacka
  [44, 57.93, 12.53],
  // Alingsås
  [45, 58.35, 11.94],
  // Uddevalla
  [46, 58.28, 12.29],
  // Trollhättan
  [50, 57.72, 12.94],
  // Borås
  [52, 58.17, 13.55],
  // Falköping
  [54, 58.39, 13.85],
  // Skövde
  [55, 57.78, 14.16],
  // Jönköping
  [58, 58.41, 15.62],
  // Linköping
  [60, 58.59, 16.19],
  // Norrköping
  [61, 58.75, 17.01],
  // Nyköping
  [63, 59.37, 16.51],
  // Eskilstuna
  [65, 59.38, 13.5],
  // Karlstad
  [70, 59.27, 15.21],
  // Örebro
  [72, 59.61, 16.55],
  // Västerås
  [75, 59.86, 17.64],
  // Uppsala
  [78, 60.48, 15.43],
  // Borlänge
  [79, 60.61, 15.63],
  // Falun
  [80, 60.67, 17.14],
  // Gävle
  [82, 61.3, 16.39],
  // Bollnäs
  [83, 63.18, 14.64],
  // Östersund
  [85, 62.39, 17.31],
  // Sundsvall
  [87, 62.63, 17.94],
  // Härnösand
  [89, 63.29, 18.72],
  // Örnsköldsvik
  [90, 63.83, 20.26],
  // Umeå
  [93, 64.75, 20.95],
  // Skellefteå
  [95, 65.58, 22.15],
  // Luleå
  [97, 65.58, 22.15],
  // Luleå
  [98, 67.86, 20.23]
  // Kiruna
];
function approximateLocation(postalCode) {
  const digits = postalCode.replace(/\D/g, "");
  if (digits.length !== 5) return null;
  const prefix = Number(digits.slice(0, 2));
  const region = REGIONS.reduce((best, item) => item[0] <= prefix ? item : best, REGIONS[0]);
  const rest = Number(digits.slice(2));
  const angle = rest * 137.5 * Math.PI / 180;
  const distance = rest % 100 / 100 * 0.07;
  return {
    lat: Math.round((region[1] + distance * Math.sin(angle)) * 1e4) / 1e4,
    lng: Math.round((region[2] + distance * 2 * Math.cos(angle)) * 1e4) / 1e4
  };
}

// src/job-drafts.ts
var DRAFT_TOKEN_HEADER = "x-draft-token";
var draftTokenSchema = z10.string().min(20).max(128);
var categorySlugSchema = z10.string().trim().max(60).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Choose a category");
var recaptchaTokenSchema = z10.string().max(4e3).default("");
var jobDraftFieldsSchema = z10.object({
  title: z10.string().trim().min(5).max(100),
  description: z10.string().trim().min(20).max(4e3),
  categorySlug: categorySlugSchema,
  postalCode: postalCodeSchema,
  wantedStart: wantedStartSchema,
  /** The pin on the form's map; optional, null removes it */
  location: jobLocationSchema.nullable().optional()
});
var createJobDraftSchema = jobDraftFieldsSchema.extend({ recaptchaToken: recaptchaTokenSchema });
var editJobSchema = jobDraftFieldsSchema.partial().refine((value) => Object.values(value).some((field) => field !== void 0), {
  message: "Nothing to change"
});
var updateJobDraftSchema = editJobSchema;
var jobDraftQuestionsSchema = z10.object({ locale: localeSchema.default("sv") });
var answerJobQuestionsSchema = z10.object({
  answers: z10.array(z10.object({ questionId: z10.string().uuid(), answer: z10.string().trim().max(500).nullable() })).min(1).max(MAX_FOLLOW_UP_QUESTIONS)
});
var submitJobDraftSchema = z10.object({
  name: z10.string().trim().min(2).max(100),
  email: z10.string().trim().toLowerCase().email().max(254),
  phone: phoneSchema,
  locale: localeSchema.default("sv"),
  acceptTerms: z10.literal(true, { message: "Accept the terms to send the job" }),
  recaptchaToken: recaptchaTokenSchema
});
function normalizeJobAnswer(question, answer) {
  if (answer === null || answer.trim() === "") return { ok: true, value: null };
  const value = answer.trim();
  switch (question.answerType) {
    case "yes_no":
      return value === "yes" || value === "no" ? { ok: true, value } : { ok: false, message: "Answer yes or no" };
    case "number": {
      const number = Number(value.replace(/\s/g, "").replace(",", "."));
      return Number.isFinite(number) && number >= 0 && number <= 1e6 ? { ok: true, value: String(number) } : { ok: false, message: "Enter a number" };
    }
    case "choice":
      return question.options?.includes(value) ? { ok: true, value } : { ok: false, message: "Choose one of the options" };
    case "text":
      return { ok: true, value };
  }
}

// src/admin-jobs.ts
import { z as z11 } from "zod";
var REVIEW_STATUSES = ["in_review", "needs_info"];
var reviewStatusSchema = z11.enum(REVIEW_STATUSES);
var adminJobListQuerySchema = paginationQuerySchema.extend({
  /** One status; both review statuses when left out */
  status: reviewStatusSchema.optional(),
  /** Matches the title, the client's name or email */
  q: z11.string().trim().max(100).optional(),
  /** Oldest first by default, so jobs are handled in the order they came */
  sort: z11.enum(["submittedAt", "-submittedAt"]).default("submittedAt")
});
var requestJobInfoSchema = z11.object({
  questions: z11.array(z11.string().trim().min(5).max(300)).min(1).max(MAX_FOLLOW_UP_QUESTIONS)
});
var rejectJobSchema = z11.object({ reason: z11.string().trim().min(3).max(500) });

// src/client-jobs.ts
import { z as z12 } from "zod";
var ACTIVE_JOB_STATUSES = [
  "in_review",
  "needs_info",
  "open"
];
var EDITABLE_JOB_STATUSES = ["in_review", "needs_info"];
var clientJobListQuerySchema = paginationQuerySchema.extend({
  filter: z12.enum(["all", "active", "closed"]).default("all"),
  /** Matches the title */
  q: z12.string().trim().max(100).optional()
});

// src/providers.ts
import { z as z13 } from "zod";
var PROVIDER_STATUSES = ["onboarding", "pending", "verified", "rejected"];
var providerStatusSchema = z13.enum(PROVIDER_STATUSES);
var EDITABLE_PROVIDER_STATUSES = [
  "onboarding",
  "rejected"
];
function luhnValid(digits) {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let digit = Number(digits[i]) * (i % 2 === 0 ? 2 : 1);
    if (digit > 9) digit -= 9;
    sum += digit;
  }
  return sum % 10 === 0;
}
var orgNumberSchema = z13.string().trim().transform((value) => value.replace(/[\s-]/g, "")).transform((value) => /^(19|20)\d{10}$/.test(value) ? value.slice(2) : value).pipe(
  z13.string().regex(/^\d{10}$/, "Enter the organisation number with 10 digits, e.g. 556321-9988").refine(luhnValid, "Check the organisation number; the last digit does not match")
);
var formatOrgNumber = (digits) => /^\d{10}$/.test(digits) ? `${digits.slice(0, 6)}-${digits.slice(6)}` : digits;
var providerSignupSchema = z13.object({
  name: z13.string().trim().min(2).max(100),
  email: z13.string().trim().toLowerCase().email().max(254),
  phone: phoneSchema,
  locale: localeSchema.default("sv"),
  acceptTerms: z13.literal(true, { message: "Accept the terms to sign up" }),
  recaptchaToken: recaptchaTokenSchema
});
var optionalText = (max) => z13.string().trim().max(max).transform((value) => value || null).nullable().optional();
var websiteSchema = z13.string().trim().max(200).transform((value) => value && !/^https?:\/\//i.test(value) ? `https://${value}` : value).pipe(
  z13.union([
    z13.literal(""),
    z13.string().url("Enter a web address, e.g. www.example.se").regex(/^https?:\/\/[^\s/]+\.[^\s/]+(\/\S*)?$/i, "Enter a web address, e.g. www.example.se")
  ])
).transform((value) => value || null).nullable().optional();
var providerCompanySchema = z13.object({
  companyName: z13.string().trim().min(2).max(120),
  orgNumber: orgNumberSchema,
  hasFSkatt: z13.literal(true, { message: "F-skatt is required to work through Hemmafixa" }),
  about: optionalText(1e3),
  website: websiteSchema
});
var countyCodeSchema = z13.string().regex(/^\d{2}$/, "Choose a county");
var unique = (list) => new Set(list).size === list.length;
var providerServicesSchema = z13.object({
  categorySlugs: z13.array(categorySlugSchema).min(1, "Choose at least one category").max(70).refine(unique, "Each category once"),
  countyCodes: z13.array(countyCodeSchema).min(1, "Choose at least one county").max(21).refine(unique, "Each county once")
});
var providerDetailsSchema = z13.object({ about: optionalText(1e3), website: websiteSchema });

// src/admin-users.ts
import { z as z14 } from "zod";
var adminUserListQuerySchema = paginationQuerySchema.extend({
  /** Matches the name or email */
  q: z14.string().trim().max(100).optional(),
  role: roleSchema.optional(),
  status: userStatusSchema.optional(),
  /** Newest accounts first by default */
  sort: z14.enum(["-createdAt", "createdAt", "-lastLoginAt"]).default("-createdAt")
});
var AUDIT_ENTITY_TYPES = ["user", "provider", "job", "report"];
var auditEntityTypeSchema = z14.enum(AUDIT_ENTITY_TYPES);
var auditLogQuerySchema = paginationQuerySchema.extend({
  entityType: auditEntityTypeSchema.optional(),
  entityId: z14.string().uuid().optional(),
  actorId: z14.string().uuid().optional(),
  /** Matches the actor's name or email, or the action ("provider.") */
  q: z14.string().trim().max(100).optional()
});

// src/admin-providers.ts
import { z as z15 } from "zod";
var adminProviderListQuerySchema = paginationQuerySchema.extend({
  /** Every status when left out */
  status: providerStatusSchema.optional(),
  /** Matches the company name, org number, contact name or email */
  q: z15.string().trim().max(100).optional(),
  /** Oldest submission first by default, so profiles are handled in the order they came */
  sort: z15.enum(["submittedAt", "-submittedAt", "-createdAt"]).default("submittedAt")
});
var rejectProviderSchema = z15.object({ reason: z15.string().trim().min(3).max(500) });

// src/proposals.ts
import { z as z16 } from "zod";
var PROPOSAL_STATUSES = [
  /** Sent; the provider can still edit or withdraw it */
  "submitted",
  /** The client accepted it and a conversation opened (P5) */
  "accepted",
  /** The client closed the job with this provider (P5) */
  "chosen",
  "declined",
  "withdrawn",
  /** The job closed (cancelled, expired or another provider chosen) */
  "closed"
];
var proposalStatusSchema = z16.enum(PROPOSAL_STATUSES);
var ACTIVE_PROPOSAL_STATUSES = [
  "submitted",
  "accepted"
];
var MAX_PROPOSAL_PRICE_ORE = 1e9;
var priceOre = z16.number().int().min(100).max(MAX_PROPOSAL_PRICE_ORE);
var optionalText2 = (max) => z16.string().trim().max(max).nullish().transform((value) => value || null);
var proposalInputSchema = z16.object({
  priceFromOre: priceOre,
  /** Set for a price range; null for a fixed price */
  priceToOre: priceOre.nullish().transform((value) => value ?? null),
  /** What the price covers: material, removal of waste, travel */
  includes: optionalText2(1e3),
  /** Earliest start, YYYY-MM-DD */
  earliestStart: z16.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use the format YYYY-MM-DD").refine((value) => !Number.isNaN(Date.parse(value)), "Not a valid date").nullish().transform((value) => value ?? null),
  durationDays: z16.number().int().min(1).max(365).nullish().transform((value) => value ?? null),
  message: z16.string().trim().min(20).max(2e3)
}).refine((input) => input.priceToOre === null || input.priceToOre > input.priceFromOre, {
  path: ["priceToOre"],
  message: "Must be higher than the lowest price"
});
var declineProposalSchema = z16.object({ reason: optionalText2(500) });
var providerProposalListQuerySchema = paginationQuerySchema.extend({
  filter: z16.enum(["all", "active", "closed"]).default("all"),
  status: proposalStatusSchema.optional()
});
var MAX_ACCEPTED_PROPOSALS = 3;
var closeJobSchema = z16.object({
  proposalId: z16.string().uuid().optional(),
  /** Why it was cancelled; only kept in the job history */
  reason: z16.string().trim().max(500).transform((value) => value || void 0).optional()
});

// src/provider-jobs.ts
import { z as z17 } from "zod";
var PROVIDER_JOB_PROPOSAL_FILTERS = ["all", "none", "sent"];
var providerJobListQuerySchema = paginationQuerySchema.extend({
  /** Matches the title and description */
  q: z17.string().trim().max(100).optional(),
  /** A main category also finds its sub-categories */
  category: z17.string().regex(/^[a-z0-9-]{1,80}$/).optional(),
  wantedStart: wantedStartSchema.optional(),
  proposal: z17.enum(PROVIDER_JOB_PROPOSAL_FILTERS).default("all"),
  /** Only the jobs the provider saved */
  saved: z17.enum(["true", "false"]).optional().transform((value) => value === "true")
});

// src/uploads.ts
import { z as z18 } from "zod";
var UPLOAD_KINDS = ["job_photo", "job_document", "provider_document", "message_file"];
var uploadKindSchema = z18.enum(UPLOAD_KINDS);
var UPLOAD_STATUSES = ["pending", "ready", "rejected"];
var MB = 1024 * 1024;
var PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
var UPLOAD_RULES = {
  job_photo: { types: PHOTO_TYPES, maxBytes: 10 * MB },
  job_document: { types: [...PHOTO_TYPES, "application/pdf"], maxBytes: 15 * MB },
  provider_document: { types: ["image/jpeg", "image/png", "application/pdf"], maxBytes: 10 * MB },
  message_file: { types: [...PHOTO_TYPES, "application/pdf"], maxBytes: 15 * MB }
};
var isCleanedPhoto = (upload) => upload.kind === "job_photo" || upload.kind === "message_file" && upload.contentType.startsWith("image/");
var fileFields = z18.object({
  fileName: z18.string().trim().min(1).max(200),
  contentType: z18.string().trim().toLowerCase().max(100),
  sizeBytes: z18.number().int().positive()
});
function checkFile({ kind, contentType, sizeBytes }, ctx) {
  const rules = UPLOAD_RULES[kind];
  if (!rules.types.includes(contentType)) {
    ctx.addIssue({
      code: z18.ZodIssueCode.custom,
      path: ["contentType"],
      message: `This kind of file is not allowed here. Allowed: ${rules.types.join(", ")}`
    });
  }
  if (sizeBytes > rules.maxBytes) {
    ctx.addIssue({
      code: z18.ZodIssueCode.custom,
      path: ["sizeBytes"],
      message: `The file is too large. The limit is ${rules.maxBytes / MB} MB`
    });
  }
}
var createUploadSchema = fileFields.extend({ kind: uploadKindSchema }).superRefine(checkFile);
var JOB_ATTACHMENT_KINDS = ["job_photo", "job_document"];
var MAX_JOB_ATTACHMENTS = 10;
var jobAttachmentInputSchema = fileFields.extend({ kind: z18.enum(JOB_ATTACHMENT_KINDS) }).superRefine(checkFile);
var uploadIdParamSchema = z18.object({ id: z18.string().uuid(), uploadId: z18.string().uuid() });
var PROVIDER_DOCUMENT_TYPES = ["company_registration", "f_skatt", "other"];
var providerDocumentTypeSchema = z18.enum(PROVIDER_DOCUMENT_TYPES);
var MAX_PROVIDER_DOCUMENTS = 10;
var providerDocumentInputSchema = fileFields.extend({ type: providerDocumentTypeSchema }).superRefine((input, ctx) => checkFile({ ...input, kind: "provider_document" }, ctx));

// src/conversations.ts
import { z as z19 } from "zod";
var CONVERSATION_STATUSES = [
  "open",
  /** The job closed; still readable */
  "closed",
  /** Admin stopped it after a report; still readable */
  "blocked"
];
var conversationStatusSchema = z19.enum(CONVERSATION_STATUSES);
var MESSAGE_KINDS = ["text", "system"];
var messageKindSchema = z19.enum(MESSAGE_KINDS);
var REPORT_TARGETS = ["message", "proposal", "provider", "user"];
var reportTargetSchema = z19.enum(REPORT_TARGETS);
var REPORT_REASONS = [
  "spam",
  "abusive",
  "fraud",
  /** Moves payment or contact off the platform before the proposal is accepted */
  "off_platform",
  "other"
];
var reportReasonSchema = z19.enum(REPORT_REASONS);
var REPORT_STATUSES = ["open", "resolved", "dismissed"];
var reportStatusSchema = z19.enum(REPORT_STATUSES);
var REPORT_ACTIONS = [
  "warned",
  "message_hidden",
  "conversation_blocked",
  "account_suspended"
];
var reportActionSchema = z19.enum(REPORT_ACTIONS);
var CONVERSATION_SIDES = ["client", "provider"];
var MAX_MESSAGE_LENGTH = 4e3;
var MAX_MESSAGE_FILES = 5;
var MESSAGE_PAGE_SIZE = 30;
var conversationListQuerySchema = paginationQuerySchema;
var messageListQuerySchema = z19.object({
  conversationId: z19.string().uuid(),
  before: z19.string().datetime({ offset: true }).optional(),
  limit: z19.coerce.number().int().min(1).max(100).default(MESSAGE_PAGE_SIZE)
});
var sendMessageSchema = z19.object({
  conversationId: z19.string().uuid(),
  body: z19.string().trim().max(MAX_MESSAGE_LENGTH).default(""),
  uploadIds: z19.array(z19.string().uuid()).max(MAX_MESSAGE_FILES).default([])
}).refine((input) => input.body.length > 0 || input.uploadIds.length > 0, {
  path: ["body"],
  message: "Write a message or add a file"
});
var SOCKET_PATH = "/api/socket.io";

// src/reports.ts
import { z as z20 } from "zod";
var createReportSchema = z20.object({
  target: reportTargetSchema,
  /** The message, proposal, provider profile or user id */
  targetId: z20.string().uuid(),
  reason: reportReasonSchema,
  details: z20.string().trim().max(1e3).transform((value) => value || void 0).optional()
});
var adminReportListQuerySchema = paginationQuerySchema.extend({
  status: reportStatusSchema.default("open"),
  target: reportTargetSchema.optional()
});
var REPORT_ACTIONS_BY_TARGET = {
  message: ["warned", "message_hidden", "conversation_blocked", "account_suspended"],
  proposal: ["warned", "account_suspended"],
  provider: ["warned", "account_suspended"],
  user: ["warned", "account_suspended"]
};
var resolveReportSchema = z20.object({
  action: reportActionSchema,
  /** Kept with the report and in the audit log; for a suspension it is the suspension reason */
  note: z20.string().trim().min(3).max(1e3)
});
var dismissReportSchema = z20.object({
  note: z20.string().trim().max(1e3).transform((value) => value || void 0).optional()
});

// src/overview.ts
var MAX_OVERVIEW_TODOS = 8;
var STATS_MONTHS = 6;

// src/billing.ts
import { z as z21 } from "zod";
var SUBSCRIPTION_STATUSES = ["incomplete", "trialing", "active", "past_due", "canceled"];
var subscriptionStatusSchema = z21.enum(SUBSCRIPTION_STATUSES);
var INVOICE_STATUSES = ["draft", "open", "paid", "uncollectible", "void"];
var invoiceStatusSchema = z21.enum(INVOICE_STATUSES);
var CREDIT_REASONS = [
  "monthly_grant",
  "proposal",
  "refund",
  "pack",
  "expiry",
  "adjustment"
];
var creditReasonSchema = z21.enum(CREDIT_REASONS);
var DEFAULT_GRACE_DAYS = 7;
var MAX_TRIAL_DAYS = 365;
var TRIAL_CREDITS = 5;
var CREDIT_PACK = { credits: 10, priceOre: 14900 };
var planInputSchema = z21.object({
  nameSv: z21.string().trim().min(1).max(60),
  nameEn: z21.string().trim().min(1).max(60),
  descriptionSv: z21.string().trim().max(500).nullable(),
  descriptionEn: z21.string().trim().max(500).nullable(),
  priceOre: z21.number().int().min(0).max(1e7),
  proposalsPerMonth: z21.number().int().min(0).max(1e4),
  trialDays: z21.number().int().min(0).max(MAX_TRIAL_DAYS),
  position: z21.number().int().min(0).max(1e3),
  active: z21.boolean()
});
var ACCESS_SUBSCRIPTION_STATUSES = [
  "trialing",
  "active",
  "past_due"
];
var billingCheckoutSchema = z21.object({ planId: z21.string().uuid() });
var adminSubscriptionListQuerySchema = paginationQuerySchema.extend({
  /** Matches the company name, org number or email */
  q: z21.string().trim().max(100).optional(),
  status: subscriptionStatusSchema.optional(),
  planId: z21.string().uuid().optional()
});
export {
  ACCESS_SUBSCRIPTION_STATUSES,
  ACTIVE_JOB_STATUSES,
  ACTIVE_PROPOSAL_STATUSES,
  ADMIN_PERMISSIONS,
  API_ERROR_CODES,
  AUDIT_ENTITY_TYPES,
  CONVERSATION_SIDES,
  CONVERSATION_STATUSES,
  CREDIT_PACK,
  CREDIT_REASONS,
  DEFAULT_GRACE_DAYS,
  DEFAULT_NOTIFICATION_PREFERENCES,
  DRAFT_TOKEN_HEADER,
  EDITABLE_JOB_STATUSES,
  EDITABLE_PROVIDER_STATUSES,
  INVOICE_STATUSES,
  JOB_ATTACHMENT_KINDS,
  JOB_QUESTION_ANSWER_TYPES,
  JOB_QUESTION_SOURCES,
  JOB_STATUSES,
  LOCALES,
  MAX_ACCEPTED_PROPOSALS,
  MAX_FOLLOW_UP_QUESTIONS,
  MAX_JOB_ATTACHMENTS,
  MAX_MARK_READ_IDS,
  MAX_MESSAGE_FILES,
  MAX_MESSAGE_LENGTH,
  MAX_OVERVIEW_TODOS,
  MAX_PAGE_SIZE,
  MAX_PROPOSAL_PRICE_ORE,
  MAX_PROVIDER_DOCUMENTS,
  MAX_TRIAL_DAYS,
  MESSAGE_KINDS,
  MESSAGE_PAGE_SIZE,
  NOTIFICATION_TYPES,
  ORE_PER_SEK,
  PROPOSAL_STATUSES,
  PROVIDER_DOCUMENT_TYPES,
  PROVIDER_JOB_PROPOSAL_FILTERS,
  PROVIDER_STATUSES,
  REPORT_ACTIONS,
  REPORT_ACTIONS_BY_TARGET,
  REPORT_REASONS,
  REPORT_STATUSES,
  REPORT_TARGETS,
  REVIEW_STATUSES,
  ROLES,
  SOCKET_PATH,
  STATS_MONTHS,
  SUBSCRIPTION_STATUSES,
  TRIAL_CREDITS,
  UPLOAD_KINDS,
  UPLOAD_RULES,
  UPLOAD_STATUSES,
  USER_STATUSES,
  WANTED_STARTS,
  adminJobListQuerySchema,
  adminPermissionSchema,
  adminProviderListQuerySchema,
  adminReportListQuerySchema,
  adminSubscriptionListQuerySchema,
  adminUserListQuerySchema,
  answerJobQuestionsSchema,
  approximateLocation,
  auditEntityTypeSchema,
  auditLogQuerySchema,
  billingCheckoutSchema,
  categorySlugSchema,
  changePasswordSchema,
  checkPasswordLinkSchema,
  clientJobListQuerySchema,
  closeJobSchema,
  conversationListQuerySchema,
  conversationStatusSchema,
  countyCodeSchema,
  createJobDraftSchema,
  createReportSchema,
  createUploadSchema,
  creditReasonSchema,
  declineProposalSchema,
  dismissReportSchema,
  draftTokenSchema,
  editJobSchema,
  followUpQuestionSchema,
  followUpSuggestionSchema,
  forgotPasswordSchema,
  formatOrgNumber,
  formatPostalCode,
  formatSek,
  idParamSchema,
  invoiceStatusSchema,
  isCleanedPhoto,
  jobAttachmentInputSchema,
  jobDraftFieldsSchema,
  jobDraftQuestionsSchema,
  jobLocationSchema,
  jobQuestionAnswerTypeSchema,
  jobQuestionSourceSchema,
  jobStatusSchema,
  localeSchema,
  loginSchema,
  markNotificationsReadSchema,
  messageKindSchema,
  messageListQuerySchema,
  newPasswordSchema,
  normalizeJobAnswer,
  notificationDataSchemas,
  notificationInputSchema,
  notificationListQuerySchema,
  notificationTypeSchema,
  optionalPhoneSchema,
  oreToSek,
  orgNumberSchema,
  paginationQuerySchema,
  passwordTokenSchema,
  percentOfOre,
  phoneSchema,
  planInputSchema,
  postalCodeParamSchema,
  postalCodeSchema,
  proposalInputSchema,
  proposalStatusSchema,
  providerCompanySchema,
  providerDetailsSchema,
  providerDocumentInputSchema,
  providerDocumentTypeSchema,
  providerJobListQuerySchema,
  providerProposalListQuerySchema,
  providerServicesSchema,
  providerSignupSchema,
  providerStatusSchema,
  reactivateUserSchema,
  recaptchaTokenSchema,
  rejectJobSchema,
  rejectProviderSchema,
  reportActionSchema,
  reportReasonSchema,
  reportStatusSchema,
  reportTargetSchema,
  requestJobInfoSchema,
  resolveReportSchema,
  reviewStatusSchema,
  roleSchema,
  sekToOre,
  sendMessageSchema,
  setPasswordSchema,
  submitJobDraftSchema,
  subscriptionStatusSchema,
  suspendUserSchema,
  toE164,
  updateJobDraftSchema,
  updateNotificationPreferencesSchema,
  updatePreferencesSchema,
  updateProfileSchema,
  uploadIdParamSchema,
  uploadKindSchema,
  userStatusSchema,
  wantedStartSchema
};
//# sourceMappingURL=index.js.map